import { useEffect, useRef, useState } from "react"

/**
 * True once the element has been scrolled into view, and true from the start
 * for anyone who has asked their system to reduce motion.
 *
 * Animations that start on page load have usually finished before the reader
 * reaches them, so the only thing they achieve is a blank space on the way
 * down. Holding each one until its section is actually on screen means the
 * movement lands when somebody is looking at it.
 */
export function useInView({ once = true, margin = "-12% 0px" } = {}) {
	const ref = useRef(null)
	const [inView, setInView] = useState(false)

	useEffect(() => {
		const node = ref.current
		if (!node) return undefined

		const reduced =
			typeof window.matchMedia === "function" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches

		if (reduced || typeof IntersectionObserver !== "function") {
			setInView(true)
			return undefined
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true)
					if (once) observer.disconnect()
				} else if (!once) {
					setInView(false)
				}
			},
			{ rootMargin: margin, threshold: 0.15 }
		)

		observer.observe(node)
		return () => observer.disconnect()
	}, [once, margin])

	return [ref, inView]
}
